// Original JavaScript Code from  Marijn Haverbeke (http://eloquentjavascript.net/1st_edition/appendix2.html)

export class BinaryHeap<T> {
  content: T[];
  scoreFunction: (x: T) => number;

  constructor(scoreFunction: (x: T) => number) {
    this.content = [];
    this.scoreFunction = scoreFunction;
  }

  push(element: T) {
    this.content.push(element);
    this.bubbleUp(this.content.length - 1);
  }

  pop(): T {
    const result = this.content[0];
    const end = this.content.pop();
    if (end && this.content.length > 0) {
      this.content[0] = end;
      this.sinkDown(0);
    }
    return result;
  }

  remove(node: T) {
    const length = this.content.length;
    // To remove a value, we must search through the array to find
    // it.
    for (let index = 0; index < length; index++) {
      if (this.content[index] !== node) continue;
      // When it is found, the process seen in 'pop' is repeated
      // to fill up the hole.
      const end = this.content.pop();
      // If the element we popped was the one we needed to remove,
      // we're done.
      if (index === length - 1) break;
      // Otherwise, we replace the removed element with the popped
      // one, and allow it to float up or sink down as appropriate.
      this.content[index] = end!;
      this.bubbleUp(index);
      this.sinkDown(index);
      break;
    }
  }

  size() {
    return this.content.length;
  }

  private bubbleUp(n: number) {
    // Fetch the element that has to be moved.
    const element = this.content[n];
    const score = this.scoreFunction(element);
    // When at 0, an element can not go up any further.
    while (n > 0) {
      // Compute the parent element's index, and fetch it.
      const parentN = Math.floor((n + 1) / 2) - 1;
      const parent = this.content[parentN];
      // If the parent has a lesser score, things are in order and we
      // are done.
      if (score >= this.scoreFunction(parent)) break;

      // Otherwise, swap the parent with the current element and
      // continue.
      this.content[parentN] = element;
      this.content[n] = parent;
      // biome-ignore lint/style/noParameterAssign:
      n = parentN;
    }
  }

  private sinkDown(n: number) {
    // Look up the target element and its score.
    const length = this.content.length;
    const element = this.content[n];
    const elementScore = this.scoreFunction(element);

    while (true) {
      // Compute the indices of the child elements.
      const child2N = (n + 1) * 2;
      const child1N = child2N - 1;
      // This is used to store the new position of the element,
      // if any.
      let swap = null;
      let child1Score = 0;
      // If the first child exists (is inside the array)...
      if (child1N < length) {
        // Look it up and compute its score.
        const child1 = this.content[child1N];
        child1Score = this.scoreFunction(child1);
        // If the score is less than our element's, we need to swap.
        if (child1Score < elementScore) {
          swap = child1N;
        }
      }
      // Do the same checks for the other child.
      if (child2N < length) {
        const child2 = this.content[child2N];
        const child2Score = this.scoreFunction(child2);
        if (child2Score < (swap == null ? elementScore : child1Score)) {
          swap = child2N;
        }
      }

      // No need to swap further, we are done.
      if (swap == null) break;

      // Otherwise, swap and continue.
      this.content[n] = this.content[swap];
      this.content[swap] = element;
      // biome-ignore lint/style/noParameterAssign:
      n = swap;
    }
  }

  public removeAll() {
    this.content = [];
  }
}
